import type { MessageKey } from './es-CL';

/**
 * Diccionario en. El tipo Record<MessageKey, string> obliga a cubrir exactamente las
 * mismas claves que es-CL: una clave nueva sin traducir rompe la compilacion.
 */
export const en: Record<MessageKey, string> = {
  'common.pay': 'Pay',
  'common.cancel': 'Cancel',
  'common.retry': 'Try again',
  'common.back': 'Back',
  'common.cancel_payment': 'Cancel payment',
  'common.secure_payment': 'Secure payment',
  'common.powered_by': 'Powered by ApiPay',
  'promo.label': 'Merchant promotions',

  'loading.session': 'Loading your payment...',

  'summary.title': 'Payment summary',
  'summary.total': 'Total to pay',
  'summary.description': 'Details',
  'summary.merchant': 'Merchant',
  'summary.expires_at': 'Valid until {date}',
  'summary.breakdown': 'Payment detail',
  'summary.units': '{quantity} x {unit}',
  'summary.item_count': '{count} items',
  'summary.breakdown_scroll': 'Payment detail, scrollable',

  'method.title': 'Choose how to pay',
  'method.continue': 'Continue to payment',
  'method.chosen': 'You will pay with {method}',
  'method.select': 'Select',
  'method.webpay': 'Webpay Plus',
  'method.oneclick': 'Webpay Oneclick',
  'method.mercadopago': 'MercadoPago',
  'method.sandbox': 'Test gateway',
  'method.globalgetnet': 'Global Getnet Web Checkout',
  'method.getnet': 'Getnet',
  'method.compraaqui': 'CompraAquí',
  'method.mach': 'MACH',
  'method.rutpay': 'RutPay',
  'method.santander': 'Santander Payment Button',
  'method.amipass': 'Amipass (benefit card)',
  'method.unknown': 'Other payment method',

  'gateway.loading': 'Preparing the payment method...',
  'gateway.unavailable': 'This payment method is not available right now.',
  'gateway.redirect_cta': 'Pay with {gateway}',
  'gateway.redirect_hint': 'We will take you to the gateway site to complete the payment securely.',
  'gateway.redirecting': 'Redirecting you...',
  'gateway.handoff': 'Taking you to {gateway}...',
  'gateway.return_hint':
    'You are back from the gateway. It confirms the outcome with us directly, so this screen updates on its own.',
  'gateway.returning': 'Confirming your payment...',
  'gateway.qr_hint': 'Scan this code with the BancoEstado app to pay.',
  'gateway.qr_waiting': 'Waiting for you to complete the payment on your phone...',
  'gateway.qr_alt': 'QR code to pay with {gateway}',
  'gateway.qr_unavailable': 'We could not render the QR code. Try again from your phone.',
  'gateway.confirm_hint':
    'You are back from the gateway. We are checking the outcome of your payment with it.',
  'gateway.check_again': 'Check again',
  'gateway.embedded_watching':
    'We are tracking your payment. This screen updates on its own as soon as we have the outcome; do not close or reload this window.',
  'gateway.embedded_not_final':
    'If the gateway form shows a notice, it is not the final outcome: the one that counts is confirmed here.',
  'gateway.embedded_3ds':
    'Your bank is checking that it is you. Complete the validation shown over this screen; do not close or reload the window.',

  'oneclick.notice':
    'You first enrol your card in the Transbank form, and the payment is then charged to it when you come back. Card details are entered ONLY in the Transbank form: ApiPay never receives or stores them.',
  'oneclick.cta': 'Enrol card and pay',
  'oneclick.opening': 'Preparing the enrolment with Transbank...',
  'oneclick.redirecting': 'Taking you to the Transbank form...',
  'oneclick.closing': 'Saving your card...',
  'oneclick.charging': 'Charging your saved card...',
  'oneclick.no_form': 'Transbank did not return the enrolment form. Please try again.',

  'processing.title': 'We are confirming your payment',
  'processing.body': 'Do not close or reload this window.',

  'requires_action.title': 'One more step',
  'requires_action.body': 'Complete the verification in your bank window.',

  'status.back_to_merchant': 'Back to the store',
  'status.auto_return': 'Returning to the store in {segundos}s',
  'status.success.title': 'Payment completed',
  'status.success.body': 'Your payment was approved successfully.',
  'status.failed.title': 'We could not process your payment',
  'status.failed.body': 'Check your payment method or try a different one.',
  'status.expired.title': 'The payment session expired',
  'status.expired.body': 'Go back to the store and start the payment again.',
  'status.reversed.title': 'This payment was voided',
  'status.reversed.body':
    'The merchant voided this charge. Returning the amount is handled by your payment provider; quote the reference below if you need to ask about it.',
  'status.blocked.title': 'We cannot show the payment here',
  'status.blocked.body': 'This checkout only works on the merchant site.',
  'status.canceled.title': 'Payment canceled',
  'status.canceled.body': 'You canceled the payment. You can try again whenever you want.',

  'error.title': 'Something went wrong',
  'error.detail_label': 'Server detail, for the merchant:',
  'error.validation_error': 'The payment data is not valid.',
  'error.invalid_api_key': 'The merchant configuration is not valid.',
  'error.invalid_client_secret': 'This payment session is no longer valid.',
  'error.insufficient_permissions': 'This payment session is not allowed to continue.',
  'error.resource_not_found': 'We could not find this payment session.',
  'error.card_declined': 'Your payment method was declined. Try a different one.',
  'error.gateway_rejected': 'The gateway declined the payment. Try a different method.',
  'error.invalid_state_transition': 'This payment session was already resolved.',
  'error.currency_not_supported': 'The charge currency is not enabled.',
  'error.amount_below_minimum': 'The amount is below the allowed minimum.',
  'error.rate_limited': 'Too many attempts. Wait a few seconds and try again.',
  'error.gateway_unavailable': 'The gateway is unavailable. Try again in a few minutes.',
  'error.internal_error': 'We had an internal problem. Please try again.',
  'error.network_error': 'We could not connect. Check your connection and try again.',
  'error.session_invalid': 'We could not start the payment session. Please try again.',
  'error.payment_failed': 'The payment could not be completed.',
  'error.intent_expired': 'The payment session expired.',
  'error.embed_origin_denied': 'This checkout is not authorized on this site.',
  'error.inscription_lost':
    'We could not resume your card enrolment. Enrol it again to complete the payment.',
  'error.unknown': 'Something went wrong. Please try again.',

  'route.not_found.title': 'Page not available',
  'route.not_found.body': 'This address does not match a payment session.',
};
